
import { TimeHorizon } from '../../types';

export interface CashflowScenario {
  id: string;
  name: string;
  description: string;
}

export interface CashflowResult {
  horizon: string;
  baseline: string;
  stressed: string;
  severe: string;
  recovery: string;
}

export interface LiquidityBucket {
  bucket: string;
  inflows: string;
  outflows: string;
  net: string;
  cumulative: string;
}

export interface RepricingGapItem {
  bucket: string;
  assets: string;
  liabilities: string;
  gap: string;
  cumulativeGap: string;
}

export interface DurationGapItem {
  category: string;
  amount: string;
  duration: string;
  weight: string;
  weightedDuration: string;
}

// Sample data for cashflow scenarios
export const defaultCashflowScenarios: CashflowScenario[] = [
  {
    id: "baseline",
    name: "Baseline",
    description: "Normal market conditions with expected cashflows",
  },
  {
    id: "stressed",
    name: "Stressed",
    description: "Moderate stress with 15% deposit outflow and 20% payment delays",
  },
  {
    id: "severe",
    name: "Severe Stress",
    description: "Severe market stress with 25% deposit outflow and 35% payment delays",
  },
  {
    id: "recovery",
    name: "Recovery",
    description: "Post-stress recovery with normalized funding conditions",
  },
];

// Sample data for cashflow results
export const defaultCashflowResults: CashflowResult[] = [
  {
    horizon: "1 Month",
    baseline: "147.50",
    stressed: "72.25",
    severe: "-35.80",
    recovery: "125.30",
  },
  {
    horizon: "3 Months",
    baseline: "285.75",
    stressed: "145.60",
    severe: "-89.25",
    recovery: "195.50",
  },
  {
    horizon: "6 Months",
    baseline: "412.30",
    stressed: "75.80",
    severe: "-156.40",
    recovery: "298.75",
  },
  {
    horizon: "12 Months",
    baseline: "635.80",
    stressed: "182.25",
    severe: "-87.90",
    recovery: "485.60",
  },
];

// Sample data for liquidity buckets
export const defaultLiquidityData: LiquidityBucket[] = [
  {
    bucket: "1-7 Days",
    inflows: "875.25",
    outflows: "750.50",
    net: "124.75",
    cumulative: "124.75",
  },
  {
    bucket: "8-14 Days",
    inflows: "520.30",
    outflows: "550.60",
    net: "-30.30",
    cumulative: "94.45",
  },
  {
    bucket: "15-30 Days",
    inflows: "685.75",
    outflows: "725.20",
    net: "-39.45",
    cumulative: "55.00",
  },
  {
    bucket: "31-90 Days",
    inflows: "950.60",
    outflows: "1040.90",
    net: "-90.30",
    cumulative: "-35.30",
  },
  {
    bucket: "91-180 Days",
    inflows: "1250.40",
    outflows: "1300.10",
    net: "-49.70",
    cumulative: "-85.00",
  },
  {
    bucket: "181-365 Days",
    inflows: "1875.25",
    outflows: "1835.60",
    net: "39.65",
    cumulative: "-45.35",
  },
  {
    bucket: "> 1 Year",
    inflows: "3250.80",
    outflows: "3130.30",
    net: "120.50",
    cumulative: "75.15",
  },
];

// Sample data for repricing gap
export const defaultRepricingGapData: RepricingGapItem[] = [
  {
    bucket: "0-1M",
    assets: "1250.50",
    liabilities: "1130.30",
    gap: "120.20",
    cumulativeGap: "120.20",
  },
  {
    bucket: "1-3M",
    assets: "1680.75",
    liabilities: "1595.60",
    gap: "85.15",
    cumulativeGap: "205.35",
  },
  {
    bucket: "3-6M",
    assets: "1825.40",
    liabilities: "1890.60",
    gap: "-65.20",
    cumulativeGap: "140.15",
  },
  {
    bucket: "6-12M",
    assets: "2150.30",
    liabilities: "2260.50",
    gap: "-110.20",
    cumulativeGap: "29.95",
  },
  {
    bucket: "1-3Y",
    assets: "3250.60",
    liabilities: "3295.75",
    gap: "-45.15",
    cumulativeGap: "-15.20",
  },
  {
    bucket: "3-5Y",
    assets: "2580.40",
    liabilities: "2500.25",
    gap: "80.15",
    cumulativeGap: "64.95",
  },
  {
    bucket: ">5Y",
    assets: "1850.75",
    liabilities: "1815.60",
    gap: "35.15",
    cumulativeGap: "100.10",
  },
];

// Sample data for duration gap
export const defaultDurationGapData: DurationGapItem[] = [
  {
    category: "Loans",
    amount: "7,850.50",
    duration: "3.25",
    weight: "0.55",
    weightedDuration: "1.79",
  },
  {
    category: "Investments",
    amount: "4,250.30",
    duration: "4.75",
    weight: "0.30",
    weightedDuration: "1.43",
  },
  {
    category: "Other Assets",
    amount: "2,150.20",
    duration: "2.95",
    weight: "0.15",
    weightedDuration: "0.44",
  },
  {
    category: "Deposits",
    amount: "8,950.75",
    duration: "2.15",
    weight: "0.63",
    weightedDuration: "1.35",
  },
  {
    category: "Borrowings",
    amount: "3,750.40",
    duration: "3.85",
    weight: "0.26",
    weightedDuration: "1.00",
  },
  {
    category: "Other Liabilities",
    amount: "1,550.30",
    duration: "1.60",
    weight: "0.11",
    weightedDuration: "0.18",
  },
];
